/**
 * PDP interactivity: variant engine, media gallery + zoom, quantity
 * selector, sticky mobile Add to Cart, size guide dialog.
 *
 * Deliberately NOT sharing code with assets/product-stories.js's Quick
 * Shop engine (see sections/product.liquid's top comment for why) —
 * this one works from server-rendered markup that's already correct on
 * load (progressive enhancement: variant switching, price and
 * availability are indexable/usable before this file even runs) and
 * enhances it in place, rather than building the whole panel from
 * scratch client-side the way Quick Shop has to.
 */

// Mirrors assets/product-stories.js's formatMoney — same "$ 1,999"
// shape as the theme's server-rendered money_without_trailing_zeros.
// Duplicated rather than extracted into a shared file: it's three
// lines, and a shared utility module isn't worth the extra request for
// this alone.
function formatMoney(cents) {
  const amount = Math.round(cents) / 100;
  return `$ ${amount.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
}

async function updateCartCount() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const badge = document.querySelector('.site-header__cart-count');
    const cartLink = document.querySelector('.site-header__cart-link');
    if (badge) {
      badge.textContent = cart.item_count;
    } else if (cartLink && cart.item_count > 0) {
      const span = document.createElement('span');
      span.className = 'site-header__cart-count';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = cart.item_count;
      cartLink.appendChild(span);
    }
    return cart;
  } catch (error) {
    return null;
  }
}

/**
 * <product-info> — the purchase panel. Reads the small per-product JSON
 * sections/product.liquid embeds (this product's variants/media only),
 * re-computes the selected variant on every option click, and updates
 * price/availability/media/URL/Add to Cart without a network request —
 * the data's already on the page.
 */
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        this.product = this.readJson('[data-product-json]');
        this.i18n = this.readJson('[data-product-i18n]') || {};
        if (!this.product) return;

        this.form = this.querySelector('[data-add-to-cart]')?.closest('form');
        this.variantIdInput = this.querySelector('[data-variant-id-input]');
        this.priceWrapper = this.querySelector('[data-price-wrapper]');
        this.addButton = this.querySelector('[data-add-to-cart]');
        this.addButtonText = this.querySelector('[data-add-to-cart-text]');
        this.addMessage = this.querySelector('[data-add-to-cart-message]');
        this.availabilityIndicator = this.querySelector('[data-availability-indicator]');
        this.optionGroups = Array.from(this.querySelectorAll('[data-option-group]'));

        this.selectedOptions = this.readInitialSelection();
        this.currentVariant = this.findVariant(this.selectedOptions);

        this.addEventListener('click', (event) => {
          const optionButton = event.target.closest('[data-option-value]');
          if (optionButton) this.onOptionClick(optionButton);
        });

        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.addToCart();
        });

        this.setupQuantity();
      }

      readJson(selector) {
        const script = document.querySelector(selector);
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return null;
        }
      }

      readInitialSelection() {
        const selected = [];
        this.optionGroups.forEach((group) => {
          const index = Number(group.dataset.optionGroup);
          const active = group.querySelector('[data-option-value][aria-pressed="true"]');
          if (active) selected[index] = active.dataset.value;
        });
        return selected;
      }

      findVariant(options) {
        if (!options.length) return this.product.variants[0];
        return this.product.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
      }

      // Matches the same combo_exists/combo_available split as
      // snippets/product-variant-picker.liquid's initial server render
      // (V8.2): a variant that EXISTS but is sold out must stay
      // selectable (Case A); only a combination with no matching variant
      // at all becomes a real disabled button (Case B).
      getVariantForOptionValue(optionIndex, value) {
        return this.product.variants.find(
          (variant) =>
            variant.options[optionIndex] === value &&
            variant.options.every((v, i) => i === optionIndex || v === this.selectedOptions[i])
        );
      }

      onOptionClick(button) {
        if (button.disabled) return;
        const group = button.closest('[data-option-group]');
        const index = Number(group.dataset.optionGroup);
        this.selectedOptions[index] = button.dataset.value;
        this.render();
      }

      render() {
        this.optionGroups.forEach((group) => {
          const index = Number(group.dataset.optionGroup);
          const valueText = group.querySelector('[data-option-selected-value]');
          if (valueText) valueText.textContent = this.selectedOptions[index];

          group.querySelectorAll('[data-option-value]').forEach((button) => {
            const isActive = button.dataset.value === this.selectedOptions[index];
            button.setAttribute('aria-pressed', String(isActive));
            button.classList.toggle('variant-picker__option--active', isActive);
            button.classList.toggle('variant-picker__swatch--active', isActive);

            // Color swatches never get disabled/sold-out-struck here —
            // they stay selectable regardless of stock (V8.2 §7-9); their
            // own sold-out indicator is computed once at server-render
            // time from ALL of that color's variants, not the current
            // selection, so JS doesn't need to (and shouldn't) touch it.
            if (button.classList.contains('variant-picker__swatch')) return;

            const matched = this.getVariantForOptionValue(index, button.dataset.value);
            const comboExists = !!matched;
            const comboAvailable = !!(matched && matched.available);
            button.disabled = !comboExists;
            button.classList.toggle('variant-picker__option--soldout', comboExists && !comboAvailable);
          });
        });

        this.currentVariant = this.findVariant(this.selectedOptions);
        this.updatePrice();
        this.updateAvailability();
        this.updateMedia();
        this.updateUrl();
        document.dispatchEvent(new CustomEvent('pdp:variant-change', { detail: { variant: this.currentVariant } }));
      }

      updatePrice() {
        if (!this.priceWrapper) return;
        const variant = this.currentVariant;
        if (!variant) {
          this.priceWrapper.innerHTML = '';
          return;
        }
        const onSale = variant.compare_at_price > variant.price;
        // Real percentage off, computed from this variant's own
        // price/compare_at_price — never a hardcoded figure, and hidden
        // outright when the new variant doesn't carry a valid compare-at
        // (V8.2 §3/§6).
        const discountPercent = onSale ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
        this.priceWrapper.innerHTML = `
          <div class="price${onSale ? ' price--on-sale' : ''}">
            ${
              onSale
                ? `<span class="price__sale">${formatMoney(variant.price)}</span><span class="price__compare-at">${formatMoney(variant.compare_at_price)}</span><span class="price__discount">-${discountPercent}%</span>`
                : `<span class="price__regular">${formatMoney(variant.price)}</span>`
            }
          </div>`;
      }

      updateAvailability() {
        const variant = this.currentVariant;
        if (this.variantIdInput) this.variantIdInput.value = variant ? variant.id : '';

        if (this.availabilityIndicator) {
          const available = !!(variant && variant.available);
          const soldOut = !!(variant && !variant.available);
          this.availabilityIndicator.classList.toggle('pdp__availability--soldout', soldOut);
          this.availabilityIndicator.innerHTML = available
            ? `<span class="pdp__availability-dot" aria-hidden="true"></span>${this.i18n.available || ''}`
            : soldOut
              ? `<span class="pdp__availability-dot pdp__availability-dot--soldout" aria-hidden="true"></span>${this.i18n.sold_out || ''}`
              : '';
        }

        if (!this.addButton) return;
        if (!variant) {
          this.addButton.disabled = true;
          if (this.addButtonText) this.addButtonText.textContent = this.i18n.unavailable || '';
        } else {
          this.addButton.disabled = !variant.available;
          if (this.addButtonText) {
            this.addButtonText.textContent = variant.available ? this.i18n.add_to_cart : this.i18n.sold_out;
          }
        }
        if (this.addMessage) this.addMessage.textContent = '';
      }

      updateMedia() {
        const variant = this.currentVariant;
        if (!variant || !variant.featured_media_id) return;
        document.dispatchEvent(new CustomEvent('pdp:media-change', { detail: { mediaId: variant.featured_media_id } }));
      }

      updateUrl() {
        const variant = this.currentVariant;
        if (!variant || !window.history?.replaceState) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      setupQuantity() {
        const wrapper = this.querySelector('[data-quantity-selector]');
        if (!wrapper) return;
        const input = wrapper.querySelector('[data-quantity-input]');
        wrapper.querySelector('[data-quantity-decrease]')?.addEventListener('click', () => {
          input.value = Math.max(1, (Number(input.value) || 1) - 1);
        });
        wrapper.querySelector('[data-quantity-increase]')?.addEventListener('click', () => {
          input.value = (Number(input.value) || 1) + 1;
        });
      }

      async addToCart() {
        if (!this.addButton || this.addButton.disabled) return;
        const quantity = Number(this.querySelector('[data-quantity-input]')?.value) || 1;
        const id = this.variantIdInput?.value || (this.currentVariant && this.currentVariant.id);
        if (!id) return;

        this.addButton.disabled = true;
        this.addButton.classList.add('button--loading');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id, quantity }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || this.i18n.error);

          if (this.addMessage) this.addMessage.textContent = this.i18n.added || '';
          updateCartCount();
          document.dispatchEvent(new CustomEvent('pdp:added-to-cart', { detail: { variantId: id, quantity } }));
        } catch (error) {
          if (this.addMessage) this.addMessage.textContent = error.message;
        } finally {
          this.addButton.disabled = !this.currentVariant?.available;
          this.addButton.classList.remove('button--loading');
        }
      }
    }
  );
}

/**
 * <product-media-gallery> — PDP V2.
 *
 * The main frame is a native horizontal scroll-snap track (touch swipe,
 * trackpad, keyboard, arrows and thumbnails all end up scrolling the same
 * track), so there is no custom pointer-swipe code that could fight the
 * vertical page scroll. The active slide is derived from the scroll
 * position, and every other control (thumbnails, counter, `inert` on
 * off-screen slides) follows it.
 *
 * Photos only (PDP V2.1): the Liquid snippet never renders videos in this gallery; they live in
 * "Míralo en acción" (assets/product-video.js). Zoom opens the high-res photo (2x on click) and steps
 * through the visible photos.
 *
 * Two modes (see snippets/product-media-gallery.liquid):
 *  - full gallery: listens for `pdp:media-change` (the variant's featured image) and scrolls to it;
 *  - gallery by colour (data-grouped="true"): every slide carries data-color-group; listens for
 *    `pdp:variant-change` and shows ONLY the slides of the selected colour (main photo first). Choosing only
 *    a different size keeps the visitor where they are.
 * Neither component knows the other's internals.
 */
if (!customElements.get('product-media-gallery')) {
  customElements.define(
    'product-media-gallery',
    class ProductMediaGallery extends HTMLElement {
      connectedCallback() {
        // Gallery by colour: every slide/thumbnail is in allItems/allThumbs; items/thumbs are the VISIBLE
        // subset (all of them in the full gallery).
        this.allItems = Array.from(this.querySelectorAll('[data-gallery-item]'));
        this.allThumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        this.items = this.allItems.filter((item) => !item.hidden);
        this.thumbs = this.allThumbs.filter((thumb) => !thumb.hidden);
        this.grouped = this.dataset.grouped === 'true';
        this.currentGroup = this.grouped ? this.dataset.initialGroup || '' : null;
        this.colorIndex = Number(this.dataset.colorIndex);
        this.lastMediaId = this.dataset.initialMedia || null;
        this.track = this.querySelector('[data-gallery-track]');
        this.thumbList = this.querySelector('[data-gallery-thumbs]');
        this.currentLabel = this.querySelector('[data-gallery-current]');
        this.index = 0;
        if (this.items.length === 0 || !this.track) return;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.allThumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => {
            const i = this.items.findIndex((item) => item.dataset.mediaId === thumb.dataset.mediaId);
            if (i !== -1) this.goTo(i);
          })
        );
        this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

        this.track.addEventListener('keydown', (event) => this.onKeydown(event));
        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

        // Keep the active slide aligned when the frame is resized (rotation,
        // breakpoint changes, sticky/column changes).
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.alignToIndex());
          this.resizeObserver.observe(this.track);
        }

        if (this.grouped) this.relabel();
        this.syncState();
        this.setupZoom();

        if (this.grouped) {
          // The server already rendered the selected colour (also with ?variant=…): its main photo is first.
          this.onVariantChange = (event) => {
            const variant = event.detail && event.detail.variant;
            const color = variant && variant.options ? variant.options[this.colorIndex] : undefined;
            // Choosing only a different SIZE keeps the same colour: applyGroup ignores it.
            if (color !== undefined) this.applyGroup(color);
          };
          document.addEventListener('pdp:variant-change', this.onVariantChange);
        } else {
          this.onMediaChange = (event) => {
            const mediaId = String(event.detail.mediaId);
            // Choosing only a different SIZE keeps the same featured image: leave the visitor where they are.
            if (mediaId === this.lastMediaId) return;
            this.lastMediaId = mediaId;
            const i = this.items.findIndex((item) => item.dataset.mediaId === mediaId);
            if (i !== -1) this.goTo(i);
          };
          document.addEventListener('pdp:media-change', this.onMediaChange);

          // Arriving with ?variant=… selected: open on that variant's image
          // (instantly — it is the initial state, not an animation).
          this.showInitialVariantMedia();
        }
      }

      disconnectedCallback() {
        if (this.onMediaChange) document.removeEventListener('pdp:media-change', this.onMediaChange);
        if (this.onVariantChange) document.removeEventListener('pdp:variant-change', this.onVariantChange);
        this.resizeObserver?.disconnect();
      }

      // Show only the slides of one colour (its own photos, then its shared photo). Never leaves the gallery
      // empty: the Liquid snippet renders at least one slide for every value of the colour option.
      applyGroup(group) {
        if (!this.grouped || group === this.currentGroup) return;
        this.currentGroup = group;
        this.allItems.forEach((item, i) => {
          const show = (item.dataset.colorGroup ?? '') === group;
          item.hidden = !show;
          if (this.allThumbs[i]) this.allThumbs[i].hidden = !show;
        });
        this.items = this.allItems.filter((item) => !item.hidden);
        this.thumbs = this.allThumbs.filter((thumb) => !thumb.hidden);
        this.reveal([...this.items, ...this.thumbs]);
        this.index = 0;
        const total = this.items.length;
        this.classList.toggle('product-gallery--single', total <= 1);
        ['[data-gallery-prev]', '[data-gallery-next]', '[data-gallery-indicator]', '.product-gallery__rail'].forEach((selector) => {
          const element = this.querySelector(selector);
          if (element) element.hidden = total <= 1;
        });
        const totalLabel = this.querySelector('[data-gallery-total]');
        if (totalLabel) totalLabel.textContent = String(total);
        this.relabel();
        this.scrollToIndex(0, 'auto');
        this.syncState();
      }

      // The images of the colours that are not selected are written as data-src / data-srcset so nothing is
      // downloaded for them; give them their real source the moment their colour is shown.
      reveal(elements) {
        elements.forEach((element) => {
          element.querySelectorAll('img[data-src]').forEach((img) => {
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.removeAttribute('data-srcset');
          });
        });
      }

      // Keep the accessible names ("3 de 6", "Ver imagen 3") in step with the visible set.
      relabel() {
        const total = this.items.length;
        const slideTemplate = this.dataset.slideLabel || '';
        const thumbTemplate = this.dataset.thumbLabel || '';
        this.items.forEach((item, i) => {
          if (slideTemplate && item.hasAttribute('aria-roledescription')) item.setAttribute('aria-label', slideTemplate.replace('__I__', i + 1).replace('__T__', total));
        });
        this.thumbs.forEach((thumb, i) => {
          if (thumbTemplate) thumb.setAttribute('aria-label', thumbTemplate.replace('__I__', i + 1));
        });
      }

      get behavior() {
        return this.reducedMotion?.matches ? 'auto' : 'smooth';
      }

      showInitialVariantMedia() {
        const params = new URLSearchParams(window.location.search);
        const variantId = params.get('variant');
        if (!variantId) return;
        const data = document.querySelector('[data-product-json]');
        if (!data) return;
        try {
          const product = JSON.parse(data.textContent);
          const variant = product.variants.find((v) => String(v.id) === variantId);
          if (!variant || !variant.featured_media_id) return;
          const i = this.items.findIndex((item) => item.dataset.mediaId === String(variant.featured_media_id));
          if (i > 0) this.goTo(i, 'auto');
        } catch (error) {
          /* leave the gallery on its first slide */
        }
      }

      goTo(index, behavior) {
        const total = this.items.length;
        const normalized = ((index % total) + total) % total;
        const wrapped = Math.abs(normalized - this.index) > 1 && total > 2 && (index < 0 || index >= total);
        this.index = normalized;
        this.programmaticTarget = normalized;
        this.scrollToIndex(normalized, wrapped ? 'auto' : behavior || this.behavior);
        this.syncState();
      }

      scrollToIndex(i, behavior) {
        const item = this.items[i];
        if (!item) return;
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior });
      }

      alignToIndex() {
        // Re-snap instantly after a resize so the slide never ends up between two positions.
        this.scrollToIndex(this.index, 'auto');
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          const width = this.track.clientWidth || 1;
          const i = Math.max(0, Math.min(this.items.length - 1, Math.round(this.track.scrollLeft / width)));
          // While a programmatic scroll is in flight, ignore intermediate slides.
          if (this.programmaticTarget != null) {
            if (i === this.programmaticTarget) this.programmaticTarget = null;
            else return;
          }
          if (i !== this.index) {
            this.index = i;
            this.syncState();
          }
        });
      }

      onKeydown(event) {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.items.length - 1);
        }
      }

      syncState() {
        this.items.forEach((item, i) => {
          const active = i === this.index;
          item.classList.toggle('product-gallery__item--active', active);
          // Off-screen slides must not be tab stops or announced.
          if (active) item.removeAttribute('inert');
          else item.setAttribute('inert', '');
        });

        this.thumbs.forEach((thumb, i) => {
          const active = i === this.index;
          thumb.classList.toggle('product-gallery__thumb--active', active);
          if (active) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        this.revealThumb(this.thumbs[this.index]);

        if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1);
      }

      // Scroll only the thumbnail rail (never the page) so the active
      // thumbnail stays visible.
      revealThumb(thumb) {
        const list = this.thumbList;
        if (!thumb || !list) return;
        // Positions are measured with bounding boxes (relative to the scrolled content) so the frame room that
        // the rail reserves around the thumbnails never skews them; the margin keeps the selection frame (6px
        // outside the photo) fully inside the visible area.
        const lr = list.getBoundingClientRect();
        const tr = thumb.getBoundingClientRect();
        const room = 10;
        const vertical = list.scrollHeight > list.clientHeight + 1 && list.scrollWidth <= list.clientWidth + 1;
        if (vertical) {
          const top = tr.top - lr.top + list.scrollTop;
          if (top < list.scrollTop + room) list.scrollTo({ top: Math.max(0, top - room), behavior: this.behavior });
          else if (top + tr.height > list.scrollTop + list.clientHeight - room) {
            list.scrollTo({ top: top + tr.height - list.clientHeight + room, behavior: this.behavior });
          }
        } else if (list.scrollWidth > list.clientWidth + 1) {
          const left = tr.left - lr.left + list.scrollLeft;
          if (left < list.scrollLeft + room || left + tr.width > list.scrollLeft + list.clientWidth - room) {
            list.scrollTo({ left: Math.max(0, left - room - 2), behavior: this.behavior });
          }
        }
      }

      /* ---------------- zoom ---------------- */

      setupZoom() {
        this.zoomDialog = this.querySelector('[data-gallery-zoom]');
        this.zoomStage = this.querySelector('[data-gallery-zoom-stage]');
        if (!this.zoomDialog || !this.zoomStage) return;

        this.querySelectorAll('[data-gallery-zoom-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', () => this.openZoom(trigger));
        });
        this.querySelector('[data-gallery-zoom-close]')?.addEventListener('click', () => this.zoomDialog.close());
        this.zoomDialog.addEventListener('click', (event) => {
          if (event.target === this.zoomDialog) this.zoomDialog.close();
        });
        this.zoomStage.addEventListener('click', (event) => {
          if (event.target.tagName === 'IMG') this.zoomStage.classList.toggle('product-zoom__stage--zoomed');
        });
        this.querySelector('[data-gallery-zoom-prev]')?.addEventListener('click', () => this.stepZoom(-1));
        this.querySelector('[data-gallery-zoom-next]')?.addEventListener('click', () => this.stepZoom(1));
        this.zoomDialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.stepZoom(-1);
          else if (event.key === 'ArrowRight') this.stepZoom(1);
        });
        this.zoomDialog.addEventListener('close', () => {
          this.zoomStage.innerHTML = '';
          this.zoomStage.classList.remove('product-zoom__stage--zoomed');
          // Return focus to the control of the slide that is active NOW: the visitor may
          // have stepped through the lightbox, and the slide they opened it from can be
          // inert (off-screen) by then.
          const active = this.items[this.index];
          const target = active?.querySelector('[data-gallery-zoom-trigger]') || this.lastZoomTrigger;
          target?.focus({ preventScroll: true });
        });
      }

      openZoom(trigger) {
        this.lastZoomTrigger = trigger || document.activeElement;
        this.renderZoom();
        this.zoomDialog.showModal();
      }

      stepZoom(delta) {
        if (this.items.length < 2) return;
        this.goTo(this.index + delta, 'auto');
        this.renderZoom();
      }

      renderZoom() {
        const item = this.items[this.index];
        if (!item) return;
        this.zoomStage.classList.remove('product-zoom__stage--zoomed');
        this.zoomStage.innerHTML = '';

        const trigger = item.querySelector('[data-gallery-zoom-trigger]');
        const image = item.querySelector('img');
        const src = trigger?.dataset.zoomSrc || image?.currentSrc || image?.src;
        if (!src) return;
        const zoomImage = document.createElement('img');
        zoomImage.src = src;
        zoomImage.alt = trigger?.dataset.zoomAlt || image?.alt || '';
        this.zoomStage.appendChild(zoomImage);
      }
    }
  );
}

/**
 * Desktop sticky gallery: publishes the gallery's rendered height as
 * --pdp-gallery-h on .pdp__media. The stylesheet uses it so a gallery that
 * is taller than the viewport sticks by its BOTTOM edge instead of becoming
 * unreachable (see .pdp__media-sticky in sections/product.liquid).
 */
(function initStickyGalleryMetrics() {
  const media = document.querySelector('.pdp__media');
  const sticky = media?.querySelector('[data-pdp-sticky]');
  if (!media || !sticky || !('ResizeObserver' in window)) return;
  const update = () => media.style.setProperty('--pdp-gallery-h', `${Math.round(sticky.getBoundingClientRect().height)}px`);
  new ResizeObserver(update).observe(sticky);
  update();
})();

/**
 * <product-details-tabs> — V8.1. Same server-rendered disclosure-button
 * + region markup at every width (button[aria-expanded] + a region, not
 * the ARIA tablist/tab/tabpanel pattern — deliberately, so no roving
 * tabindex / arrow-key handling is needed). Only the interaction model
 * changes with viewport, checked at click time:
 *   - desktop (>=990px): single-open, like tabs — opening one closes
 *     the rest.
 *   - mobile: independent accordion — each trigger toggles only itself.
 */
if (!customElements.get('product-details-tabs')) {
  customElements.define(
    'product-details-tabs',
    class ProductDetailsTabs extends HTMLElement {
      connectedCallback() {
        this.entries = Array.from(this.querySelectorAll('[data-details-trigger]')).map((trigger) => ({
          trigger,
          panel: this.querySelector(`#${trigger.getAttribute('aria-controls')}`),
          wrapper: trigger.closest('.pdp-details__section'),
        }));

        this.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-details-trigger]');
          if (trigger) this.onTriggerClick(trigger);
        });
      }

      onTriggerClick(trigger) {
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        if (isDesktop) {
          if (isOpen) return;
          this.entries.forEach(({ trigger: t, panel, wrapper }) => {
            const active = t === trigger;
            t.setAttribute('aria-expanded', String(active));
            if (panel) panel.hidden = !active;
            wrapper?.classList.toggle('pdp-details__section--active', active);
          });
        } else {
          const entry = this.entries.find((e) => e.trigger === trigger);
          if (!entry) return;
          entry.trigger.setAttribute('aria-expanded', String(!isOpen));
          if (entry.panel) entry.panel.hidden = isOpen;
        }
      }
    }
  );
}

/**
 * Sticky mobile Add to Cart — shown only while the real ATC button is
 * out of view (IntersectionObserver), synced to the current variant via
 * the same pdp:variant-change event <product-info> dispatches.
 */
(function initStickyAtc() {
  const bar = document.querySelector('[data-sticky-atc]');
  const sourceButton = document.querySelector('[data-add-to-cart]');
  if (!bar || !sourceButton) return;

  const priceEl = bar.querySelector('[data-sticky-atc-price]');
  const button = bar.querySelector('[data-sticky-atc-button]');
  const buttonText = bar.querySelector('[data-sticky-atc-text]');

  function sync() {
    const sourcePrice = document.querySelector('[data-price-wrapper]');
    if (priceEl && sourcePrice) priceEl.innerHTML = sourcePrice.innerHTML;
    if (button) button.disabled = sourceButton.disabled;
    if (buttonText) {
      const sourceText = document.querySelector('[data-add-to-cart-text]');
      if (sourceText) buttonText.textContent = sourceText.textContent;
    }
  }

  button?.addEventListener('click', () => sourceButton.closest('form')?.requestSubmit());
  document.addEventListener('pdp:variant-change', sync);
  sync();

  const observer = new IntersectionObserver(
    (entries) => {
      const isSourceVisible = entries[0].isIntersecting;
      bar.hidden = false;
      bar.classList.toggle('pdp-sticky-atc--visible', !isSourceVisible);
    },
    { rootMargin: '0px' }
  );
  observer.observe(sourceButton);
})();

/**
 * Size guide dialog — plain open/close, no state beyond that.
 */
(function initSizeGuide() {
  const trigger = document.querySelector('[data-size-guide-trigger]');
  const dialog = document.querySelector('.size-guide');
  if (!trigger || !dialog) return;

  trigger.addEventListener('click', () => dialog.showModal());
  dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', () => trigger.focus());
})();

/**
 * Reviews safety net. sections/product.liquid hides the Loox section when the
 * product's rating count metafield says "no reviews". If Loox nevertheless
 * reports a real widget height (e.g. the metafield has not synced yet), show
 * the section again so existing reviews are never hidden. Loox's empty state
 * reserves ~144px; a rendered review list is well above 220px.
 */
(function initReviewsSafetyNet() {
  const pdp = document.querySelector('.pdp[data-reviews="0"]');
  if (!pdp) return;

  const check = () => {
    const box = document.querySelector('#looxReviews');
    if (!box) return false;
    const height = parseInt(box.style.height, 10) || 0;
    if (height > 220) {
      pdp.dataset.reviews = '1';
      return true;
    }
    return false;
  };

  if (check()) return;
  const root = document.querySelector('main') || document.body;
  const observer = new MutationObserver(() => {
    if (check()) observer.disconnect();
  });
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['style'] });
  setTimeout(() => observer.disconnect(), 20000);
})();
